import { writeFile } from "node:fs/promises";
import path from "node:path";
import { z } from "zod";
import type { PromptContent, PromptOverride, ReviewInput, ReviewPlanner } from "./agent.js";
import { runClaudeAgent } from "./agent-process.js";
import type { ClaudeCodeModel } from "./config.js";
import { logger } from "./log.js";
import { resolvePromptOverride } from "./prompt.js";
import { reviewPlanSchema, toClaudeJsonSchema, type ReviewPlan } from "./schema.js";

const log = logger.child("planner");

const envelopeSchema = z.object({
  is_error: z.boolean().optional(),
  result: z.string().optional(),
  structured_output: z.unknown().optional(),
}).loose();

export interface ClaudeCodePlannerOptions {
  executable?: string;
  model?: ClaudeCodeModel;
  prompt?: PromptContent | PromptOverride;
}

export class ClaudeCodePlanner implements ReviewPlanner {
  constructor(private readonly options: ClaudeCodePlannerOptions = {}) {}

  async plan(input: ReviewInput): Promise<ReviewPlan> {
    const settingsPath = path.join(input.runtimeDirectory, "planner-settings.json");
    const outputPath = path.join(input.runtimeDirectory, "planner-output.json");
    await writeFile(settingsPath, JSON.stringify({
      permissions: {
        allow: ["Read", "Glob", "Grep", "Bash(git diff *)", "Bash(git status *)"],
        deny: ["Write", "Edit", "Bash(git push *)", "Bash(git commit *)", "Bash(rm *)", "WebFetch", "WebSearch"],
      },
    }, null, 2));

    const defaultPrompt = buildPlannerPrompt(input);
    const prompt = await resolvePromptOverride(defaultPrompt, this.options.prompt, input);
    const executable = this.options.executable ?? "claude";
    const args = [
      "-p", prompt,
      "--append-system-prompt", "You are Revuiw's planning stage. Inspect repository evidence without modifying it. Produce a focused, factual test plan for a separate browser-review agent.",
      "--output-format", "stream-json",
      "--verbose",
      "--json-schema", toClaudeJsonSchema(reviewPlanSchema),
      "--settings", settingsPath,
      "--permission-mode", "dontAsk",
    ];
    if (this.options.model) args.push("--model", this.options.model);

    const done = log.step(`Starting planning agent (${executable}${this.options.model ? `, model ${this.options.model}` : ""})`);
    const { envelope: rawEnvelope, stdout, stderr, code, timedOut } = await runClaudeAgent({
      executable, args, cwd: input.cwd, timeoutMs: input.timeoutMs, label: "planning agent", log,
    });
    await writeFile(outputPath, stdout || stderr);

    if (timedOut) throw new Error(`Planning agent timed out after ${input.timeoutMs}ms`);
    if (code !== 0) throw new Error(`Planning agent exited with code ${code}: ${stderr.trim()}`);
    if (rawEnvelope === undefined) throw new Error("Planning agent produced no result");
    const envelope = envelopeSchema.parse(rawEnvelope);
    if (envelope.is_error) throw new Error(envelope.result ?? "Planning agent reported an execution error");
    const plan = reviewPlanSchema.parse(envelope.structured_output);
    await writeFile(path.join(input.runtimeDirectory, "plan.json"), JSON.stringify(plan, null, 2));
    done(`Planning agent finished: ${plan.workflows.length} workflow(s), ${plan.relevantRoutes.length} route(s), ${plan.risks.length} risk(s)`);
    return plan;
  }
}

export function buildPlannerPrompt(input: ReviewInput): string {
  return `Inspect the pull request diff at ${input.pullRequestDiffPath} and the relevant application source under ${input.cwd}.

Build context for a separate UI reviewer. Identify changed user-facing areas, likely routes, concrete workflows to exercise, regression risks, required setup or state, and concise review instructions. Do not perform the browser review, modify files, or claim that behavior is broken. Prefer evidence from the diff and source over guesses.`;
}
